<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      In this activity, you will learn about using bite marks as a source of evidence. Then, you’ll
      complete 2 tasks related to what you’ve learned - let's get started!
    </p>
    <p class="mb-5 text-bold">Bite marks: reliable evidence or chomping at the bit?</p>
    <p class="mb-5">
      There’s been a lot of debate over the reliability of bitemark evidence in recent years.
    </p>
    <p class="mb-5">
      With a number of high-profile miscarriages of justice* due to incorrect convictions based on
      bite marks, many now argue that this field has been completely discredited. As a result, the
      acceptability of bite marks as evidence varies between jurisdictions around the world.
    </p>
    <p class="mb-5">
      For example, the video below from the California Innocence Project argues that bite mark
      analysis is complete ‘junk science’.
    </p>
    <p class="mb-5">Take a look and see what you think.</p>
    <p class="mb-5 text-italic">
      *Miscarrige of justice is when a grossly unfair outcome results from a civil or a criminal
      judicial proceeding. For example when a person is convicted and punished for a crime that they
      did not actually commit.
    </p>
    <responsive-youtube-wrapper :video="videos[1].url" :image="videos[1].image" />

    <p class="mb-5">
      Even though the ‘error rate’ is notoriously high with this approach, it is worth learning more
      about it if you’re interested in working in forensics.
    </p>
    <p class="mb-5">
      While it isn’t recommended that a case be built on bitemark analysis alone, it may sometimes
      be used as one tool among many in an investigator’s toolbox.
    </p>
    <p class="mb-5">
      The video below goes into this in more detail and discusses the significant limitations of
      using bite marks as evidence.
    </p>
    <responsive-youtube-wrapper :video="videos[2].url" :image="videos[2].image" />

    <h4 class="mb-5">Task 1: Perform a Bitemark Analysis</h4>
    <p class="mb-5">
      The best way to get a handle on how bitemark analysis works is to perform a little bitemark
      analysis yourself.
    </p>
    <p class="mb-5">You will need:</p>
    <ul class="default-list">
      <li>
        Small disposable foam plates (you can get these from the “Party” section of the supermarket)
      </li>
      <li>A permanent marker</li>
      <li>A ruler</li>
      <li>Overhead plastic transparencies or similar (cut into quarters will work best).</li>
    </ul>
    <p class="mb-5 text-bold">Make an Impression</p>
    <p class="mb-5">Now, follow the instructions below to get your bitemark impression.</p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <ol class="numbered-list">
          <li>Grab a foam plate and permanent marker</li>
          <li>Bend the plate in half so that the top of the plate is on the inside.</li>
          <li>
            Label one half with "maxilla" (the upper jaw) and one half with "mandible" (the lower
            jaw).
          </li>
          <li>
            Put the folded plate into your mouth as far as possible and bite down on it to make a
            good impression (not too hard or you might break through the plate!)
          </li>
          <li>
            Take a piece of clear plastic transparency and a permanent marker, then place the
            plastic on top of your impression and use your permanent marker to trace the dental
            pattern onto the plastic as accurately as possible
          </li>
          <li>
            Note any unique characteristics like slanted teeth, spaces, evidence of braces, etc.
          </li>
        </ol>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/forensic_science/a2_sidebar.png"
          alt="Two photos of a folded white foam plate with a bite impression, labelled Maxilla on the top half and Mandible on the bottom half; in the second photo the dental pattern has been traced over in green marker."
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>

    <p class="mb-5">
      Once you have taken an impression of your upper and lower jaw/teeth, upload a photo of the
      unfolded plate with the impressions in it.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="1"
      filetype="media"
    />

    <p class="mb-5">
      Now, take a photo of the plastic transparency where you have traced out your dental pattern.
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="2"
      filetype="media"
    />

    <p class="mb-5 text-bold">Next …… Take Your Measurements</p>
    <p class="mb-5">Now you have your bite mark impression it’s time to take some measurements!</p>
    <p class="mb-5">
      Use a ruler to measure the width and depth of the impressions on both jaws, then complete the
      table below. For accuracy, use millimetres as your unit of measurement.
    </p>
    <p class="mb-5">
      <u>Note:</u> You can also make any notes regarding unique characteristics you found in your
      teeth.
    </p>

    <table class="table default-table">
      <tr class="subheader">
        <th scope="col" width="25%">Jaw</th>
        <th scope="col" width="25%">Width (mm)</th>
        <th scope="col" width="25%">Depth (mm)</th>
        <th scope="col" width="25%">Unique Characteristics</th>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader" width="10%">Maxilla<br />(upper jaw)</th>
        <td v-for="index in 3" :key="'q1_' + index">
          <textarea-block
            v-model="data['question1_' + index]"
            :editable="isEditable"
            iclass="'mb-0'"
          />
        </td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader" width="10%">Mandible<br />(lower jaw)</th>
        <td v-for="index in 3" :key="'q2_' + index">
          <textarea-block
            v-model="data['question2_' + index]"
            :editable="isEditable"
            iclass="'mb-0'"
          />
        </td>
      </tr>
    </table>

    <h4 class="mb-5">Task 2: Match the Bite!</h4>
    <p class="mb-5">In this task, see if you can tell your bite marks apart from other peoples’.</p>
    <p class="mb-5">You will need:</p>

    <ul class="default-list">
      <li>Some disposable rubber gloves (for hygiene)</li>
      <li>
        Some soft chewy lollies or gum (large enough so that you can get people to put full bite
        marks in them
      </li>
    </ul>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <ol class="numbered-list">
          <li>
            Give a lolly or piece of gum to each person in your household or some friends (including
            yourself)
          </li>
          <li>Ask each person to bite into their chewy lolly and leave a bite mark impression</li>
          <li>
            Do the same with your piece, but be careful not to look at the indent your teeth made on
            it
          </li>
          <li>
            Place all the pieces on a plate (make sure they are mixed up so you don’t know which one
            is yours)
          </li>
          <li>
            Now use the bite mark impression you made on the plate to see if you can match it with
            your piece!
          </li>
        </ol>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/forensic_science/a2_sidebar2.png"
          alt="Four orange chewy lollies laid out on a dark surface, each with a curved bite mark impression pressed into it."
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>

    <p class="mb-5">Could you tell which bite mark was yours?</p>
    <switch-block v-model="data.question3" :editable="isEditable" />

    <p class="mb-5">
      Take a photo of all your lollies/gum that people have bitten into and left their bite marks
      (put them all on a plate for the photo) and upload it below.
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="3"
      filetype="media"
    />

    <p class="mb-5">
      (Activities adapted from:
      <a href="https://sciencespot.net/Media/FrnsScience/bitemarkID.pdf" target="_blank"
        >https://sciencespot.net/Media/FrnsScience/bitemarkID.pdf</a
      >)
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/activity_2_vid_1_splash.jpg'),
          url: 'https://www.youtube.com/embed/IzsZbO0rRMk',
        },
        2: {
          image: require('@/assets/images/experiences/forensic_science/activity_2_vid_2_splash.jpg'),
          url: 'https://www.youtube.com/embed/Hks5_0lciR8',
        },
      },
      image1: require('@/assets/images/experiences/fitness/HIIT-Formula-Infographic-1.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
